import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { catchError, map, of } from 'rxjs';
import { SubscriptionAccessService } from '@core/services/subscription-access.service';
import { TenantSessionService } from '@core/services/tenant-session.service';
import { hasAdminRole } from '@modules/my-account/utils/account-role.utils';

function resolveRedirectUrl(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }

  const candidate = String(value).trim();
  if (!candidate.startsWith('/')) {
    return null;
  }

  if (candidate.startsWith('/pricing') || candidate.startsWith('/login')) {
    return null;
  }

  return candidate;
}

export const pricingAccessGuard: CanActivateFn = (route) => {
  const router = inject(Router);
  const subscriptionAccessService = inject(SubscriptionAccessService);
  const tenantSessionService = inject(TenantSessionService);
  const redirectUrl = resolveRedirectUrl(route.queryParamMap.get('from'));
  const reason = route.queryParamMap.get('reason');
  const authToken = tenantSessionService.getAuthToken();

  if (!authToken) {
    return of(true);
  }

  // 'billing' is a voluntary visit from Plan y facturación to pay or change plan; ADMIN only.
  if (reason === 'billing') {
    return of(
      hasAdminRole(tenantSessionService.getCurrentUser()) ? true : router.parseUrl('/'),
    );
  }

  // Feature-gated redirects (upgrade/coming-soon) must not bounce back to the blocked route.
  // Only subscription-level blocks should auto-redirect after access is restored.
  if (reason === 'upgrade' || reason === 'coming-soon') {
    return of(true);
  }

  return subscriptionAccessService.validateCurrentAccess({ force: true }).pipe(
    map((hasAccess) => {
      if (hasAccess) {
        subscriptionAccessService.clearBlockState();
        return router.parseUrl(redirectUrl ?? '/');
      }

      return true;
    }),
    catchError(() => of(true)),
  );
};
